import React, { useState } from 'react';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Target, FileText, BarChart, ArrowRight, ArrowLeft, CheckCircle, ShoppingCart } from 'lucide-react';
import CorporateHeader from '../CorporateHeader';
import Footer from '../Footer';
import Breadcrumb from '../Breadcrumb';

const AuthorGrowthSystemsPage = () => {
  const navigate = useNavigate();
  const [purchasingId, setPurchasingId] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handlePurchase = async (packageId) => {
    setPurchasingId(packageId);
    try {
      const originUrl = window.location.origin;
      const response = await fetch(`${API_URL}/api/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_id: packageId, origin_url: originUrl })
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Unable to process payment. Please try again.');
    } finally {
      setPurchasingId(null);
    }
  };

  const products = [
    {
      id: 'ecosystem',
      packageId: 'complete_ecosystem',
      title: 'The Ultimate Author Social Media eCoSystem',
      description: '400+ verified platforms where millions of readers are waiting to discover YOUR book. The most comprehensive author marketing database available.',
      price: '$97',
      originalPrice: '$174',
      badge: 'BEST VALUE — SAVE $77',
      image: 'https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/ir99bh6p_THE%20ULTIMATE%20AUTHOR%20INSTAGRAM%20eCoSystem%20front%20cover.png',
      features: [
        '400+ verified marketing platforms',
        'Facebook, Instagram, TikTok guides',
        'Author Resource Guide',
        'Case Study Blueprint',
        'Lifetime access'
      ],
      cta: 'Get Full Bundle - $97',
      highlight: true
    },
    {
      id: 'facebook',
      packageId: 'facebook_ecosystem',
      title: 'Facebook eCoSystem Guide',
      description: 'Master Facebook marketing for authors. Grow your readership and sell more books through strategic Facebook presence.',
      price: '$27',
      image: 'https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/rnk6gz3y_THE%20Ultimate%20Authors%20FACEbook%20cover.png',
      features: [
        'Platform-specific strategies',
        'Group marketing tactics',
        'Ad campaign templates',
        'Engagement frameworks'
      ],
      cta: 'Buy Now - $27',
    },
    {
      id: 'instagram',
      packageId: 'instagram_ecosystem',
      title: 'Instagram eCoSystem Guide',
      description: 'Build your author brand on Instagram. Visual storytelling and engagement strategies for book promotion.',
      price: '$27',
      image: 'https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/ir99bh6p_THE%20ULTIMATE%20AUTHOR%20INSTAGRAM%20eCoSystem%20front%20cover.png',
      features: [
        'Visual branding guide',
        'Hashtag strategies',
        'Reel & Story templates',
        'Collaboration tactics'
      ],
      cta: 'Buy Now - $27',
    },
    {
      id: 'tiktok',
      packageId: 'tiktok_ecosystem',
      title: 'TikTok eCoSystem Guide',
      description: 'Leverage BookTok and viral content to reach millions of readers. The fastest growing platform for book discovery.',
      price: '$27',
      image: 'https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/g4dpspzt_Ultimate%20Author%20TIKTOK%20eCoSystem%20front%20cover.png',
      features: [
        'BookTok strategies',
        'Viral content formulas',
        'Trend riding techniques',
        'Community building'
      ],
      cta: 'Buy Now - $27',
    },
    {
      id: 'resource-guide',
      packageId: 'website_ecosystem',
      title: 'Author Resource Guide',
      description: 'Comprehensive resource guide packed with tools, templates, and strategies for author success.',
      price: '$27',
      image: 'https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/l3735rry_AUTHOR%20RESOURCE%20GUIDE%20-%20FINAL.png',
      features: [
        'Publishing checklist',
        'Marketing templates',
        'Launch timeline',
        'Resource directory'
      ],
      cta: 'Buy Now - $27',
    },
    {
      id: 'case-study',
      packageId: null,
      title: 'Case Study Blueprint',
      description: 'Learn how the Ultimate Author eCoSystem creates bestsellers through real case studies and proven strategies. FREE with full bundle purchase.',
      price: 'FREE with Bundle',
      image: 'https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/46y75gw2_FREE%20-%20HOW%20THE%20ULTIMATE%20AUTHOR%20eCoSystem%20creates%20BestSellers.png',
      features: [
        'Real success stories',
        'Strategy breakdowns',
        'Implementation guides',
        'Results analysis'
      ],
      cta: 'Get with Bundle',
    },
  ];

  return (
    <div className="min-h-screen bg-black">
      <CorporateHeader />
      
      {/* Hero */}
      <section className="pt-28 pb-20 bg-gradient-to-b from-green-900/30 via-black to-black relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-green-600/20 via-transparent to-transparent"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Breadcrumb Navigation */}
          <Breadcrumb items={[
            { label: 'Divisions', path: null },
            { label: 'Author Growth Systems' }
          ]} />
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
            <div>
              <p className="text-green-400 text-sm font-semibold uppercase tracking-wider">Division</p>
              <h1 className="text-4xl lg:text-5xl font-black text-white">Author Growth Systems</h1>
            </div>
          </div>
          
          <p className="text-lg text-gray-500 italic mb-4">
            Featuring the Ultimate Author Social Media eCoSystem
          </p>
          
          <p className="text-xl text-gray-400 max-w-3xl leading-relaxed">
            Marketing infrastructure for modern authors. Comprehensive systems, strategies, and resources to grow your readership and sell more books.
          </p>
        </div>
      </section>

      {/* Products */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {products.map((product) => (
              <div 
                key={product.id}
                className={`bg-gray-900/50 border rounded-2xl overflow-hidden transition-all duration-300 group ${
                  product.highlight 
                    ? 'border-green-600/50 ring-1 ring-green-600/20 md:col-span-2 lg:col-span-3' 
                    : 'border-gray-800 hover:border-green-600/30'
                }`}
              >
                {product.highlight ? (
                  /* Flagship Product - Full Width */
                  <div className="grid md:grid-cols-2 gap-0">
                    <div className="aspect-square md:aspect-auto">
                      {product.image && (
                        <img 
                          src={product.image} 
                          alt={product.title}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="p-8 flex flex-col justify-center">
                      {product.badge && (
                        <span className="inline-block bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-4 w-fit">
                          {product.badge}
                        </span>
                      )}
                      <h3 className="text-2xl font-bold text-white mb-3">{product.title}</h3>
                      <p className="text-gray-400 mb-6 leading-relaxed">{product.description}</p>
                      
                      {product.features && (
                        <ul className="space-y-2 mb-6">
                          {product.features.map((feature, idx) => (
                            <li key={idx} className="flex items-center text-gray-300">
                              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      )}
                      
                      <div className="flex items-center gap-4 flex-wrap">
                        <div>
                          <span className="text-3xl font-bold text-green-400">{product.price}</span>
                          {product.originalPrice && (
                            <span className="text-gray-500 line-through ml-2">{product.originalPrice}</span>
                          )}
                        </div>
                        <Button 
                          onClick={() => product.packageId ? handlePurchase(product.packageId) : navigate('/author-ecosystem')}
                          disabled={purchasingId === product.packageId}
                          className="bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-3"
                          data-testid="buy-bundle-btn"
                        >
                          {purchasingId === product.packageId ? (
                            <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />Processing...</>
                          ) : (
                            <>{product.cta}<ArrowRight className="w-4 h-4 ml-2" /></>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Individual Product Card */
                  <>
                    <div className="aspect-square">
                      {product.image ? (
                        <img 
                          src={product.image} 
                          alt={product.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-green-900 to-emerald-900 flex items-center justify-center">
                          <BookOpen className="w-20 h-20 text-white/30" />
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-bold text-white mb-2 group-hover:text-green-400 transition-colors">{product.title}</h3>
                      <p className="text-gray-400 text-sm mb-4 line-clamp-2">{product.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-green-400 font-bold text-xl">{product.price}</span>
                        <Button 
                          onClick={() => product.packageId ? handlePurchase(product.packageId) : handlePurchase('complete_ecosystem')}
                          disabled={purchasingId === product.packageId}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white font-semibold"
                          data-testid={`buy-${product.id}-btn`}
                        >
                          {purchasingId === product.packageId ? (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          ) : (
                            <><ShoppingCart className="w-4 h-4 mr-1" />{product.cta}</>
                          )}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-b from-black to-green-900/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Grow Your Author Platform?</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            Get instant access to 400+ marketing platforms and start reaching millions of potential readers today.
          </p>
          <Button 
            onClick={() => navigate('/author-ecosystem')}
            className="bg-green-600 hover:bg-green-700 text-white font-bold px-8 py-4 text-lg"
          >
            Get the eCoSystem - $97
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default AuthorGrowthSystemsPage;
